<template>
  <div v-if="customers" class="full-height">
    <v-container v-if="!loading" class="full-height">
      <v-row class="py-4">
        <v-col cols="1">
          <v-btn
            icon
            @click="homepage"
          >
            <v-icon size="32" color="third">mdi-arrow-left</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="11" class="text-center">
          <h2>Customers</h2>
        </v-col>
      </v-row>
      <v-card v-if="!componentLoader">
        <v-card-text>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            color="third"
          />
          <v-data-table
            id="participantsDataTable"
            :headers="headers"
            :items="customersAllowedProducts"
            :search="search"
            :custom-filter="filterCustomerDataTable"
            class="mt-3"
          >
            <template #item.product="{ item }">
              <td class="text-start">{{ item.product.name }}</td>
            </template>
            <template #item.actions="{ item }">
              <div v-if="isAcceptedOrRejected(item.status.value) && canUserPerformAction">
                <!-- <span>{{ statusText(item) }}</span> -->
                <v-btn color="info" small @click="openOnDemandModal(item, true)">Info</v-btn>
              </div>
              <div v-else-if="canUserPerformAction">
                <v-btn color="info" small @click="openOnDemandModal(item)">traiter</v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
      <div v-else>
        <v-skeleton-loader
          class="mx-auto"
          max-width="300"
          type="card"
        />
      </div>
      <OnDemandModal />
    </v-container>
  </div>
</template>

<script>
import { computed, ref, onBeforeMount, provide } from '@vue/composition-api';
import router from '@/router';
import store from '@/store';
import { formatDate, sendNotification, EventBus } from '../helpers/utils';
import { onDemandRequest, deleteOnDemandRequest, getOnDemandCustomers } from '../helpers/request';
import { allowedProductStatus } from '../helpers/variables';
import OnDemandModal from '../components/customer/onDemandModal.vue';

export default {
  components: { OnDemandModal },
  setup () {
    const onDemandCustomers = ref([]);
    const loading = computed(() => store.state.loading);
    const products = computed(() => store.state.products);
    const showModal = ref(false);
    const rejectedReason = ref(null);
    const componentLoader = ref(false);

    const customers = computed(() => (onDemandCustomers.value || []).filter(customer => (customer?.allowed_products || []).some(ap => ap.status === 'PENDING' || ap.type === 'ON_DEMAND')));

    const customersAllowedProducts = computed(() => {
      const rows = [];

      onDemandCustomers.value.forEach(customer => {
        const allowedProducts = (customer?.allowed_products || []).filter(ap => ap.status === 'PENDING' || ap.type === 'ON_DEMAND');
        if (allowedProducts.length) {
          allowedProducts.forEach(ap => {
            const product = products.value.find(p => p.uuid === ap.product_uuid);
            if (product) {
              rows.push({
                firstname: customer.firstname,
                lastname: customer.lastname,
                fullname: `${customer.firstname} ${customer.lastname}`,
                email: customer.email,
                mobilephone: customer.mobilephone,
                birth_date: formatDate(customer.birth_date, 'dd/MM/yyyy'),
                product: {
                  uuid: ap.product_uuid,
                  name: product ? product.name : ''
                },
                status: { value: ap.status, label: ap.status_label },
                customer_uuid: customer.uuid,
                updated_at: ap.updated_at,
                justification: ap.justification,
                monitor_justification: ap.monitor_justification,
                monitor: ap.monitor
              });
            }
          });
        }
      });

      return rows;
    });

    const search = ref(null);
    const headers = [
      { text: 'Nom', value: 'lastname' },
      { text: 'Prenon', value: 'firstname' },
      { text: 'Date de Naissance', value: 'birth_date' },
      { text: 'Produit demandé', value: 'product' },
      { text: 'Statut', value: 'actions' },
      { text: '', value: 'buttons' }
    ];
    const homepage = () => {
      router.push({ path: '/' });
    };

    onBeforeMount(async () => {
      componentLoader.value = true;
      const customerResponse = await getOnDemandCustomers();

      if (customerResponse.data && customerResponse.data.length) {
        if (!store.state.isLazyLoaded.products) {
          await store.dispatch('getProducts');
        }
        onDemandCustomers.value = onDemandCustomers.value.concat(customerResponse.data);
      }
      componentLoader.value = false;
    });

    const filterCustomerDataTable = (value, searchValue, item) => (item.fullname || '').toLowerCase().includes(searchValue.toLowerCase() || (item.email || '').toLowerCase().includes(searchValue.toLowerCase()));

    const isAcceptedOrRejected = status => status === 'ACCEPTED' || status === 'REJECTED';

    const statusText = allowedProduct => {
      const date = allowedProduct.updated_at ? formatDate(allowedProduct.updated_at, 'dd/MM/yyyy') : '';
      const hour = allowedProduct.updated_at ? formatDate(allowedProduct.updated_at, "HH'h'mm") : '';
      return `La demande a été ${allowedProduct.status.label} le ${date} à ${hour} par moniteur 1`;
    };

    const selectedProduct = ref(null);

    const updateOnDemand = (allowedProduct, action) => {
      if (!store.getters.canUserPerformAction) {
        return;
      }
      const auxArray = [...onDemandCustomers.value];
      const index = auxArray.findIndex(c => c.uuid === allowedProduct.customer_uuid);
      if (index !== -1) {
        const allowedIndex = auxArray[index].allowed_products.findIndex(ap => ap.product_uuid === allowedProduct.product.uuid);

        if (allowedIndex !== -1) {
          switch (action) {
            case 'ACCEPTED':
            case 'REJECTED': {
              auxArray[index].allowed_products[allowedIndex] = {
                ...auxArray[index].allowed_products[allowedIndex],
                status: action,
                monitor_justification: allowedProduct.monitor_justification,
                monitor: allowedProduct.monitor
              };
              break;
            }
            case 'DELETE': {
              auxArray[index].allowed_products.splice(allowedIndex, 1);
              break;
            }
            default:
          }
          onDemandCustomers.value = auxArray;
        }
      }
    };

    const currentUserFields = computed(() => store.getters.currentUserField);

    const updateAllowedProduct = async (allowedProduct, status) => {
      if (!store.getters.canUserPerformAction) {
        return;
      }
      // TODO: change when login implement
      const customer = { ...currentUserFields.value };
      // delete customer.name;
      const response = await onDemandRequest(allowedProduct.customer_uuid, allowedProduct.product.uuid, status, customer, allowedProduct.justification || null);
      const notification = { success: false, text: 'Demande rejetée' };
      if (response.success) {
        notification.text = 'Demande acceptée';
        notification.success = true;
        updateOnDemand(allowedProduct, status);
      }
      const type = notification.success ? 'success' : 'error';
      sendNotification(type, notification.text);
    };

    const handleRequest = (allowedProduct, reason, status) => {
      const params = {
        ...allowedProduct,
        monitor_justification: reason
      };
      updateAllowedProduct(params, status);
    };

    const deleteOnDemand = async item => {
      if (!store.getters.canUserPerformAction) {
        return;
      }
      const response = await deleteOnDemandRequest(item.customer_uuid, item.product.uuid);
      const notification = { success: false, text: response.message || '' };

      if (response.success) {
        notification.success = true;
        updateOnDemand(item, 'DELETE');
      }

      const type = notification.success ? 'success' : 'error';
      sendNotification(type, notification.text);
    };

    const openOnDemandModal = (item, editing = false) => {
      if (!store.getters.canUserPerformAction) {
        return;
      }
      EventBus.$emit('show-on-demand-modal', { editing, item });
    };

    const canUserPerformAction = computed(() => store.getters.canUserPerformAction);

    provide('deleteOnDemand', deleteOnDemand);
    provide('handleRequest', handleRequest);

    return { canUserPerformAction, componentLoader, handleRequest, openOnDemandModal, deleteOnDemand, selectedProduct, rejectedReason, showModal, allowedProductStatus, customersAllowedProducts, homepage, loading, search, headers, customers, filterCustomerDataTable, formatDate, isAcceptedOrRejected, statusText };
  }
};
</script>

<style>
  #participantsDataTable > div > table{
    table-layout: auto;
  }
</style>
